import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const BottleneckTable = () => {
  const [sortBy, setSortBy] = useState('impact');
  const [sortOrder, setSortOrder] = useState('desc');

  const bottlenecks = [
    {
      id: 1,
      component: 'Payment Gateway',
      issue: 'High response latency',
      impact: 'high',
      affectedUsers: 1250,
      avgResponseTime: '3.2s',
      errorRate: '12%',
      status: 'investigating',
      assignee: 'Payment Team',
      detectedAt: new Date(Date.now() - 1800000),
      estimatedFix: '2 hours'
    },
    {
      id: 2,
      component: 'Database Cluster',
      issue: 'Connection pool exhaustion',
      impact: 'critical',
      affectedUsers: 3400,
      avgResponseTime: '5.1s',
      errorRate: '8%',
      status: 'active',
      assignee: 'Database Team',
      detectedAt: new Date(Date.now() - 3600000),
      estimatedFix: '4 hours'
    },
    {
      id: 3,
      component: 'CDN Edge Servers',
      issue: 'Cache miss rate spike',
      impact: 'medium',
      affectedUsers: 890,
      avgResponseTime: '1.8s',
      errorRate: '3%',
      status: 'monitoring',
      assignee: 'Infrastructure Team',
      detectedAt: new Date(Date.now() - 900000),
      estimatedFix: '1 hour'
    },
    {
      id: 4,
      component: 'Search Service',
      issue: 'Index rebuild causing delays',
      impact: 'medium',
      affectedUsers: 650,
      avgResponseTime: '2.4s',
      errorRate: '5%',
      status: 'scheduled',
      assignee: 'Search Team',
      detectedAt: new Date(Date.now() - 2700000),
      estimatedFix: '30 minutes'
    },
    {
      id: 5,
      component: 'API Gateway',
      issue: 'Rate limiting threshold reached',
      impact: 'low',
      affectedUsers: 320,
      avgResponseTime: '1.2s',
      errorRate: '2%',
      status: 'resolved',
      assignee: 'API Team',
      detectedAt: new Date(Date.now() - 5400000),
      estimatedFix: 'Resolved'
    }
  ];

  const getImpactColor = (impact) => {
    switch (impact) {
      case 'critical': return 'text-error bg-error/10 border-error';
      case 'high': return 'text-warning bg-warning/10 border-warning';
      case 'medium': return 'text-primary bg-primary/10 border-primary';
      case 'low': return 'text-success bg-success/10 border-success';
      default: return 'text-muted-foreground bg-muted/10 border-border';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-error';
      case 'investigating': return 'text-warning';
      case 'monitoring': return 'text-primary';
      case 'scheduled': return 'text-accent';
      case 'resolved': return 'text-success';
      default: return 'text-muted-foreground';
    }
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  const sortedBottlenecks = [...bottlenecks]?.sort((a, b) => {
    let aValue, bValue;
    
    switch (sortBy) {
      case 'impact':
        const impactOrder = { critical: 4, high: 3, medium: 2, low: 1 };
        aValue = impactOrder?.[a?.impact];
        bValue = impactOrder?.[b?.impact];
        break;
      case 'users':
        aValue = a?.affectedUsers;
        bValue = b?.affectedUsers;
        break;
      case 'response':
        aValue = parseFloat(a?.avgResponseTime);
        bValue = parseFloat(b?.avgResponseTime);
        break;
      case 'error':
        aValue = parseFloat(a?.errorRate);
        bValue = parseFloat(b?.errorRate);
        break;
      default:
        aValue = a?.detectedAt;
        bValue = b?.detectedAt;
    }

    if (sortOrder === 'asc') {
      return aValue > bValue ? 1 : -1;
    }
    return aValue < bValue ? 1 : -1;
  });

  const formatTimeAgo = (timestamp) => {
    const diff = Date.now() - timestamp?.getTime();
    const hours = Math.floor(diff / 3600000);
    const minutes = Math.floor((diff % 3600000) / 60000);
    
    if (hours > 0) return `${hours}h ${minutes}m ago`;
    return `${minutes}m ago`;
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 space-y-4 sm:space-y-0">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Performance Bottlenecks</h3>
          <p className="text-sm text-muted-foreground">
            Identified performance issues and resolution status
          </p>
        </div>

        <div className="flex items-center space-x-2">
          <Button variant="outline" size="sm" iconName="RefreshCw">
            Refresh
          </Button>
          <Button variant="ghost" size="sm" iconName="Filter">
            Filter
          </Button>
        </div>
      </div>
      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left py-3 px-2">
                <button
                  onClick={() => handleSort('component')}
                  className="flex items-center space-x-1 text-sm font-medium text-muted-foreground hover:text-foreground"
                >
                  <span>Component</span>
                  <Icon name="ArrowUpDown" size={12} />
                </button>
              </th>
              <th className="text-left py-3 px-2">
                <button
                  onClick={() => handleSort('impact')}
                  className="flex items-center space-x-1 text-sm font-medium text-muted-foreground hover:text-foreground"
                >
                  <span>Impact</span>
                  <Icon name="ArrowUpDown" size={12} />
                </button>
              </th>
              <th className="text-left py-3 px-2">
                <button
                  onClick={() => handleSort('users')}
                  className="flex items-center space-x-1 text-sm font-medium text-muted-foreground hover:text-foreground"
                >
                  <span>Affected Users</span>
                  <Icon name="ArrowUpDown" size={12} />
                </button>
              </th>
              <th className="text-left py-3 px-2">
                <button
                  onClick={() => handleSort('response')}
                  className="flex items-center space-x-1 text-sm font-medium text-muted-foreground hover:text-foreground"
                >
                  <span>Response Time</span>
                  <Icon name="ArrowUpDown" size={12} />
                </button>
              </th>
              <th className="text-left py-3 px-2">
                <button
                  onClick={() => handleSort('error')}
                  className="flex items-center space-x-1 text-sm font-medium text-muted-foreground hover:text-foreground"
                >
                  <span>Error Rate</span>
                  <Icon name="ArrowUpDown" size={12} />
                </button>
              </th>
              <th className="text-left py-3 px-2">Status</th>
              <th className="text-left py-3 px-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedBottlenecks?.map((bottleneck) => (
              <tr key={bottleneck?.id} className="border-b border-border hover:bg-muted/50">
                <td className="py-3 px-2">
                  <div>
                    <p className="font-medium text-foreground">{bottleneck?.component}</p>
                    <p className="text-xs text-muted-foreground">{bottleneck?.issue}</p>
                  </div>
                </td>
                <td className="py-3 px-2">
                  <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium border ${getImpactColor(bottleneck?.impact)}`}>
                    {bottleneck?.impact?.toUpperCase()}
                  </span>
                </td>
                <td className="py-3 px-2">
                  <span className="font-medium text-foreground">
                    {bottleneck?.affectedUsers?.toLocaleString()}
                  </span>
                </td>
                <td className="py-3 px-2">
                  <span className="text-sm text-foreground">
                    {bottleneck?.avgResponseTime}
                  </span>
                </td>
                <td className="py-3 px-2">
                  <span className="text-sm text-foreground">
                    {bottleneck?.errorRate}
                  </span>
                </td>
                <td className="py-3 px-2">
                  <div>
                    <span className={`text-sm font-medium ${getStatusColor(bottleneck?.status)}`}>
                      {bottleneck?.status?.charAt(0)?.toUpperCase() + bottleneck?.status?.slice(1)}
                    </span>
                    <p className="text-xs text-muted-foreground">
                      {formatTimeAgo(bottleneck?.detectedAt)}
                    </p>
                  </div>
                </td>
                <td className="py-3 px-2">
                  <div className="flex items-center space-x-1">
                    <Button variant="ghost" size="sm" iconName="Eye">
                      View
                    </Button>
                    <Button variant="ghost" size="sm" iconName="AlertTriangle">
                      Escalate
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Summary */}
      <div className="mt-6 pt-4 border-t border-border">
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center space-x-4 text-muted-foreground">
            <span>Total Issues: {bottlenecks?.length}</span>
            <span>Critical: {bottlenecks?.filter(b => b?.impact === 'critical')?.length}</span>
            <span>Active: {bottlenecks?.filter(b => b?.status === 'active')?.length}</span>
          </div>
          <Button variant="ghost" size="sm" iconName="Download">
            Export Report
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BottleneckTable;